import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Holiday } from '@domain/entities/Holiday';
import { DayOfWeek } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import { Card, Chip, EmptyState, Spinner, cssVars, cx } from '../../design-system';
import { MONTHS_LONG, WEEKDAYS_SHORT } from '../../format';
import styles from './HolidaysSection.module.css';
import { loadFailed } from '../../loadError';

/**
 * Feriados del país, shown beneath the area calendar.
 *
 * Grouped by month rather than listed flat, because the question this answers is
 * "when is the next long weekend" — and a flat list of eleven dates makes that
 * arithmetic the reader's problem.
 *
 * Only months that actually have a holiday get a card. Rendering twelve cards
 * with seven of them empty would bury the seven that matter.
 */

/** What a holiday's weekday buys you, which is the reason to scan this list. */
const bonusFor = (holiday: Holiday): { label: string; tone: 'weekend' | 'bridge' | 'none' } => {
  const day = holiday.date.dayOfWeek;

  if (day === DayOfWeek.Saturday || day === DayOfWeek.Sunday) {
    return { label: 'Cae en fin de semana', tone: 'none' };
  }
  if (day === DayOfWeek.Monday || day === DayOfWeek.Friday) {
    return { label: 'Fin de semana largo', tone: 'weekend' };
  }
  if (day === DayOfWeek.Thursday) return { label: 'Puente con el viernes', tone: 'bridge' };
  if (day === DayOfWeek.Tuesday) return { label: 'Puente con el lunes', tone: 'bridge' };
  return { label: 'Entre semana', tone: 'none' };
};

export const HolidaysSection = ({ year }: { year: number }): ReactNode => {
  const today = getServices().clock.today();

  const holidays = useQuery({
    queryKey: ['calendar', 'holidayList', year],
    queryFn: async () => {
      const result = await getServices().holidays.listForYear(year);
      if (!result.ok) throw loadFailed('No pudimos cargar los feriados.', result.error);
      return result.value;
    },
  });

  const list = (holidays.data ?? []).filter((h) => h.enabled);

  const byMonth = new Map<number, Holiday[]>();
  for (const holiday of list) {
    const month = holiday.date.month;
    byMonth.set(month, [...(byMonth.get(month) ?? []), holiday]);
  }

  const months = [...byMonth.entries()]
    .map(([month, items]) => ({
      month,
      items: [...items].sort((a, b) => a.date.compare(b.date)),
    }))
    .sort((a, b) => a.month - b.month);
  const rowsPerMonth = Math.max(1, ...months.map((group) => group.items.length));

  const onWorkday = list.filter((h) => !h.date.isWeekend).length;
  const longWeekends = list.filter((h) => bonusFor(h).tone === 'weekend').length;

  return (
    <section className={styles.section}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Feriados del país</h2>
          <p className={styles.note}>
            México · descansos obligatorios de la LFT · {year}. Los feriados activos no se
            descuentan de tus días de vacaciones.
          </p>
        </div>

        <div className={styles.stats}>
          <span className={styles.stat} style={cssVars({ '--stat-color': 'var(--color-brand)' })}>
            <strong className={styles.statValue}>{list.length}</strong> Feriados activos
          </span>
          <span className={cx(styles.stat, styles.statWork)}>
            <strong className={styles.statValue}>{onWorkday}</strong> Caen en día hábil
          </span>
          <span className={cx(styles.stat, styles.statBonus)}>
            <strong className={styles.statValue}>{longWeekends}</strong> Fines de semana largos
          </span>
        </div>
      </header>

      {holidays.isLoading ? (
        <Card>
          <Spinner label="Cargando feriados" />
        </Card>
      ) : months.length === 0 ? (
        <Card>
          <EmptyState
            glyph="★"
            title={`Sin feriados activos en ${String(year)}`}
            note="Un superusuario los enciende desde Configuración de la empresa."
          />
        </Card>
      ) : (
        <div className={styles.grid}>
          {months.map((group) => (
            <div key={group.month} className={styles.monthCard}>
              <div className={styles.monthHead}>
                <span className={styles.monthName}>{MONTHS_LONG[group.month - 1]}</span>
                <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
                  {group.items.length} {group.items.length === 1 ? 'feriado' : 'feriados'}
                </Chip>
              </div>

              <ul className={styles.dayList}>
                {group.items.map((holiday) => {
                  const bonus = bonusFor(holiday);
                  const isToday = holiday.date.equals(today);
                  const isPast = holiday.date.isBefore(today);
                  return (
                    <li
                      key={holiday.id}
                      className={cx(
                        styles.dayRow,
                        isPast && styles.dayRowPast,
                        isToday && styles.dayRowToday,
                      )}
                      aria-current={isToday ? 'date' : undefined}
                    >
                      <div className={styles.dayTop}>
                        <div
                          className={styles.dateTile}
                          style={cssVars({
                            /* Hoy gana sobre el fin de semana: es la pregunta más
                               inmediata que esta lista contesta, y es un día al
                               año. */
                            '--date-tile-bg': isToday
                              ? 'linear-gradient(140deg, var(--color-success), var(--color-success-ink))'
                              : holiday.date.isWeekend
                                ? 'linear-gradient(140deg, #8b97a8, #5c6a7d)'
                                : 'linear-gradient(140deg, var(--color-brand), var(--color-brand-darkest))',
                          })}
                        >
                          <span className={styles.dateWeekday}>
                            {WEEKDAYS_SHORT[holiday.date.dayOfWeek]}
                          </span>
                          <span className={styles.dateNumber}>{holiday.date.day}</span>
                        </div>
                        <div className={styles.dayText}>
                          <span className={styles.dayName}>
                            {holiday.name}
                            {/* Dicho con palabras y no sólo con color: el tinte de
                                «ya pasó» es la única diferencia entre dos filas
                                por lo demás idénticas, y eso no lo lee todo el
                                mundo ni lo anuncia ningún lector de pantalla. */}
                            {isToday && <span className={styles.todayTag}>Hoy</span>}
                          </span>
                          <Chip
                            background={
                              holiday.type === 'Obligatorio'
                                ? 'var(--color-success-bg)'
                                : 'var(--color-surface-subtle-alt)'
                            }
                            foreground={
                              holiday.type === 'Obligatorio'
                                ? 'var(--color-success-ink)'
                                : 'var(--color-text-tertiary-alt)'
                            }
                          >
                            {holiday.type === 'Obligatorio'
                              ? 'Descanso obligatorio'
                              : 'Por costumbre'}
                          </Chip>
                        </div>
                      </div>

                      <span
                        className={cx(
                          styles.bonus,
                          bonus.tone === 'weekend' && styles.bonusWeekend,
                          bonus.tone === 'bridge' && styles.bonusBridge,
                        )}
                      >
                        {bonus.label}
                      </span>
                    </li>
                  );
                })}
                {Array.from({ length: rowsPerMonth - group.items.length }, (_, index) => (
                  <li
                    key={`empty-${String(group.month)}-${String(index)}`}
                    className={cx(styles.dayRow, styles.dayRowEmpty)}
                    aria-hidden="true"
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
